<template>
  <div class="m-image-request-and-copyright">
    <!-- if open content: display download dropdown -->
    <div
      v-if="showOpenAccessForm"
      class="m-image-request-and-copyright__button"
    >
      <DropdownButton @toggle-icon="toggleIcon">
        <template #button>
          <DownloadImageButton
            :image-size="imageSize"
            :show-menu="showMenu"
            @download="requestDownload"
          />
        </template>
        <template #dropdownMenu="{ isOpen }">
          <ImageDownloadMenu
            :id="id"
            :artist="artist"
            :is-open="isOpen"
            :caption-details="captionDetails"
            :manifest-url="manifestUrl"
            :show-manifest="openContent"
            :orientation="orientation"
            :media-info="mediaInfo"
            @button-label-ready="updateLabel"
          />
        </template>
      </DropdownButton>
    </div>
    <!-- if not open content: display order image button -->
    <div v-else class="m-image-request-and-copyright__order-link">
      <!-- order button -->
      <GuiLink
        ref="buttonLabel"
        button="inverted"
        :to="legalPageRoute"
        class="m-image-request-and-copyright__order-button"
        data-testid="a-order-image-button"
        @click="logEvent"
      >
        {{ orderImageText }}
      </GuiLink>
    </div>
    <!-- Copyright Statement or Rights Statement -->
    <Copyright
      :object-copyright-statement="objectCopyrightStatement"
      :media-info="mediaInfo"
    />
  </div>
</template>

<script>
import DownloadImageButton from "./downloadImageButton/index.vue";
import DropdownButton from "@/components/molecules/dropdownButton/index.vue";
import ImageDownloadMenu from "@/components/organisms/imageDownloadMenu/index.vue";
import Copyright from "./copyright.vue";
import download from "@/mixins/download/index.vue";
import { GuiLink, useLocalization } from "@thegetty-private/getty-ui";

import {
  summarizeLicenses,
  OPEN_CONTENT
} from "@thegetty-private/linked-data.js";

export default {
  name: "ImageRequestAndCopyright",
  components: {
    Copyright,
    DownloadImageButton,
    DropdownButton,
    ImageDownloadMenu,
    GuiLink
  },
  mixins: [download],
  provide() {
    return {
      analyticsListener: (event, url, to) => {
        this.logEvent(to);
      }
    };
  },
  props: {
    /**
     * The an array of artists (string)
     */
    artist: {
      type: Array,
      required: true
    },
    /**
     * The caption object for the modal order forms
     */
    captionDetails: {
      type: Object,
      required: true
    },
    /**
     * The Human Made Object Copyright Statement
     */
    objectCopyrightStatement: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * Whether this object falls under the open content policy
     */
    openContent: {
      type: Boolean
    },
    /**
     * Whether we're in portrait or landscape orientation
     */
    orientation: {
      type: String,
      required: false,
      default: "Portrait"
    },
    /**
     * the indexedId of the object
     */
    id: {
      type: String,
      required: true
    },
    /**
     * The URL for the manifest
     */
    manifestUrl: {
      type: String,
      default: undefined
    },
    /**
     * The Visual Item representation of the selected item
     */
    mediaInfo: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Image download size (to display on button if applicable)
       */
      imageSize: undefined,
      /**
       * whether or not to display the dropdown menu (in button)
       */
      showMenu: false,
      /**
       * The download url
       */
      downloadUrl: undefined,
      /**
       * the filename for the download
       */
      downloadFilename: undefined,
      /**
       * The order image button (translated) text
       */
      orderImageText: this.translatePart("object_page.order_image")
    };
  },
  computed: {
    /**
     * Whether or not the image is under copyright
     *
     * @returns {boolean}
     */
    inCopyright() {
      let rightsStatementId = this.mediaInfo?.rightsStatementId;
      if (rightsStatementId) {
        // if the rights statement ID doesn't include the No Copyright url, return true
        return summarizeLicenses([rightsStatementId]) != OPEN_CONTENT;
      }
      return true;
    },
    /**
     * Creates the target route for the legal page (for study or publication requests)
     *
     * @returns {object} - the route for the legal page
     */
    legalPageRoute() {
      let selectedImageUrl = this.mediaInfo.serviceUrl;

      return {
        name: "legal",
        query: {
          id: [this.id],
          imageUrl: [selectedImageUrl]
        }
      };
    },
    /**
     * Whether to show the open access download form or the copyright request form
     *
     * @returns {boolean}
     */
    showOpenAccessForm() {
      return (
        this.manifestUrl &&
        this.inCopyright == false &&
        this.mediaInfo?.canDownload
      );
    }
  },
  methods: {
    /**
     * Updates prop that triggers what menu icon to display
     * based on the event emitted by dropdown menu button
     */
    toggleIcon() {
      this.showMenu = !this.showMenu;
    },
    /**
     * Updates the dropdown button label with a formatted resolution string
     * based on event emitted by the download menu
     */
    updateLabel(dimensions) {
      this.imageSize = dimensions.label.substring(
        dimensions.label.indexOf("(") + 1,
        dimensions.label.lastIndexOf(")")
      );
      this.downloadUrl = dimensions.url;
      this.downloadFilename = dimensions.filename;
    },
    /**
     * Initiate a download
     */
    requestDownload() {
      const url = this.downloadUrl;
      // Adds link to document, clicks to initiate download,
      // and sends a download event to analytics system.
      // The method comes from the download mixin
      this.downloadImage(document, url, this.id, "Open Content Form");
    },
    /**
     * Log a click event
     * @param {to} route
     * @param {string} url
     */
    logEvent(to) {
      if (to == undefined || to.name != "legal") {
        return;
      }
      if (to.query && to.query.id) {
        let id = to.query.id[0];

        this.eventBus.emit("AnalyticsEvent", {
          action: "image request click",
          category: `Image Request (${to.name})`,
          label: id,
          noninteraction: true
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
